import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { AdminCalendarSiloSelect } from './AdminCalendarSiloSelect'
import { CourtReporterFilterControls } from './CourtReporterFilterControls'
import { FilterControls } from './FilterControls'
import { useAdminCalendarSilo } from './useAdminCalendarSilo'
import { useFilteredCalendarEvents } from './useFilteredCalendarEvents'
import { useFilteredCourtReporterEvents } from './useFilteredCourtReporterEvents'

import { CalendarCourtReporterAdminEvent, ReporterSilo } from '@/apiClient'
import { View } from '@/components/Calendar/Calendar'
import { CalendarEvent } from '@/components/Calendar/CalendarEvent'
import { CourtReporterCalendarEvent } from '@/components/Calendar/CourtReporterCalendarEvent'
import { CalendarScreen } from '@/components/CalendarScreen'
import {
  CalendarParams,
  toCalendarParamsPath,
  useCalendarParams,
} from '@/queries/useCalendarParams'
import { NotFound } from '@/routes/NotFound'
import { Event } from '@/types'

export const Dashboard = () => {
  const calendarParams = useCalendarParams()
  const { silo } = useAdminCalendarSilo()

  if (!calendarParams) {
    return <NotFound />
  }

  return silo === ReporterSilo.court_reporter ? (
    <CourtReporterDashboardScreen calendarParams={calendarParams} />
  ) : (
    <DashboardScreen calendarParams={calendarParams} />
  )
}

const useOnCalendarStateChange = () => {
  const history = useHistory()
  // Keeps the selected view and the feature flags across calendar pages.
  const { search } = useLocation()

  return ({ date, view }: { date: Date; view: View }) => {
    if (view === 'listCustom') {
      return
    }

    history.push(`/admin/calendar/${toCalendarParamsPath(view, date)}${search}`)
  }
}

const DashboardScreen: React.FC<{
  calendarParams: CalendarParams
}> = ({ calendarParams }) => {
  const onStateChange = useOnCalendarStateChange()

  const { events, filters, setFilters, isFetching, refetch } =
    useFilteredCalendarEvents({
      calendarParams,
      initialFilters: {
        general_status_in: [
          'available',
          'booked',
          'past',
          'waiting',
          'unavailable',
        ],
        reporter_status_in: ['available', 'booked', 'past', 'waiting'],
      },
    })

  return (
    <CalendarScreen
      events={events ?? []}
      initialView={calendarParams.view}
      initialDate={calendarParams.date}
      renderEvent={(event, { view }) => (
        <CalendarEvent event={event as Event} view={view} onChange={refetch} />
      )}
      onStateChange={onStateChange}
      isLoading={isFetching}
      hotkeysEnabled={true}
      headerAction={<AdminCalendarSiloSelect />}
      sidebar={
        <>
          <FilterControls values={filters} onChange={setFilters} />
        </>
      }
    />
  )
}

const CourtReporterDashboardScreen: React.FC<{
  calendarParams: CalendarParams
}> = ({ calendarParams }) => {
  const onStateChange = useOnCalendarStateChange()

  const { events, filters, setFilters, isFetching, refetch } =
    useFilteredCourtReporterEvents({
      calendarParams,
      initialFilters: {
        general_status_in: ['available', 'requested', 'booked', 'past'],
      },
    })

  return (
    <CalendarScreen
      events={events}
      initialView={calendarParams.view}
      initialDate={calendarParams.date}
      renderEvent={(event, { view }) => (
        <CourtReporterCalendarEvent
          event={event as CalendarCourtReporterAdminEvent}
          view={view}
          onChange={refetch}
        />
      )}
      onStateChange={onStateChange}
      isLoading={isFetching}
      hotkeysEnabled={true}
      headerAction={<AdminCalendarSiloSelect />}
      sidebar={
        <>
          <CourtReporterFilterControls values={filters} onChange={setFilters} />
        </>
      }
    />
  )
}
